import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConfigType } from '@pflegenavi/web-components';
import type {
  ReceiptBatchFilterKey,
  ReceiptBatchFiltersState,
} from './useReceiptBatchFilters';

/**
 * The filter pills for the service provider receipt batch table. Each item
 * reads and writes the URL-backed `filtersState`.
 *
 * @example
 * const filters = useReceiptBatchFilterDefinitions(filtersState);
 * makeConfig({ filters, columns: [...] })
 */
export function useReceiptBatchFilterDefinitions(
  filtersState: ReceiptBatchFiltersState
): NonNullable<ConfigType<never, never, ReceiptBatchFilterKey>['filters']> {
  const { t } = useTranslation();
  const { values, set } = filtersState;

  return useMemo(
    () => ({
      variant: 'pills',
      items: [
        {
          key: 'receiptDate',
          type: 'dateRange',
          label: t(
            'service-provider-app.receipt-batch-overview-table.filters.receiptDate'
          ),
          range: values.receiptDate,
          onChange: (range) => set('receiptDate', range),
        },
        {
          key: 'acceptedDate',
          type: 'dateRange',
          label: t(
            'service-provider-app.receipt-batch-overview-table.filters.acceptedDate'
          ),
          range: values.acceptedDate,
          onChange: (range) => set('acceptedDate', range),
        },
      ],
    }),
    [t, values, set]
  );
}
